import { useState, useRef, useCallback, useEffect } from 'react';

interface SpeechToTextOptions {
  /** Language code, e.g. 'en-US', 'fil-PH' for Filipino */
  language?: string;
  /** Whether to return interim results as the user speaks */
  interimResults?: boolean;
  /** Called with each finalised chunk of transcript text */
  onResult?: (transcript: string) => void;
  /** Called when the user has stopped speaking (silence detected or manual stop) */
  onSpeechEnd?: (fullTranscript: string) => void;
  /** Called when an error occurs */
  onError?: (error: string) => void;
  /**
   * How many milliseconds of silence after a final result before auto-stopping.
   * Set to 0 to disable auto-stop (user must tap mic again). Default 1500ms.
   */
  silenceTimeoutMs?: number;
}

interface SpeechToTextReturn {
  /** Start listening. Returns false if unsupported or already started. */
  start: () => boolean;
  /** Stop listening and finalise the current transcript. */
  stop: () => void;
  /** Current live transcript (interim + final). */
  transcript: string;
  /** Whether the mic is actively listening. */
  isListening: boolean;
  /** Whether the SpeechRecognition API is supported in this browser. */
  isSupported: boolean;
  /** Any error message from a failed recognition attempt. */
  error: string | null;
}

/**
 * Hook into the browser-native Web Speech API (SpeechRecognition) for
 * speech-to-text. Falls back cleanly when the API is unavailable.
 *
 * The returned `start()` function also requests microphone permission implicitly
 * (the browser prompts the user).
 *
 * Includes **silence detection**: when the user stops speaking for
 * `silenceTimeoutMs`, the hook auto-stops and fires `onSpeechEnd`.
 */
export function useSpeechToText(options: SpeechToTextOptions = {}): SpeechToTextReturn {
  const {
    language = 'en-US',
    interimResults = true,
    silenceTimeoutMs = 1500,
  } = options;

  const [transcript, setTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fullTranscriptRef = useRef('');

  // Keep callbacks in refs so the recognition instance is never recreated
  const onResultRef = useRef(options.onResult);
  onResultRef.current = options.onResult;

  const onSpeechEndRef = useRef(options.onSpeechEnd);
  onSpeechEndRef.current = options.onSpeechEnd;

  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;

  const silenceTimeoutRef = useRef(silenceTimeoutMs);
  silenceTimeoutRef.current = silenceTimeoutMs;

  const isSupported =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  // Build the recognition instance **once**
  useEffect(() => {
    if (!isSupported) return;

    const SpeechRecognitionCtor =
      window.SpeechRecognition ?? (window as any).webkitSpeechRecognition;

    const recognition = new SpeechRecognitionCtor();
    recognition.lang = language;
    recognition.interimResults = interimResults;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let final = '';
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (final) {
        fullTranscriptRef.current += (fullTranscriptRef.current ? ' ' : '') + final;
        onResultRef.current?.(final);

        // Reset silence timer on each final result
        if (silenceTimeoutRef.current > 0) {
          clearSilenceTimer();
          silenceTimerRef.current = setTimeout(() => {
            if (!recognitionRef.current) return;
            try {
              recognitionRef.current.stop();
            } catch {
              // ignore
            }
            setIsListening(false);
            clearSilenceTimer();
            const finalTranscript = fullTranscriptRef.current;
            fullTranscriptRef.current = '';
            onSpeechEndRef.current?.(finalTranscript);
          }, silenceTimeoutRef.current);
        }
      }

      // Show full accumulated transcript (past finals + current interim)
      const display = interim
        ? fullTranscriptRef.current + ' ' + interim
        : fullTranscriptRef.current;
      setTranscript(display);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const msg = event.error;
      setError(msg);
      setIsListening(false);
      clearSilenceTimer();
      onErrorRef.current?.(msg);
    };

    recognition.onend = () => {
      clearSilenceTimer();
      setIsListening((prev) => {
        if (prev) {
          const finalTranscript = fullTranscriptRef.current;
          fullTranscriptRef.current = '';
          onSpeechEndRef.current?.(finalTranscript);
          return false;
        }
        return false;
      });
    };

    recognitionRef.current = recognition;

    return () => {
      clearSilenceTimer();
      try {
        recognition.abort();
      } catch {
        // ignore
      }
    };
    // Only recreate when these truly stable values change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupported, language, interimResults]);

  const start = useCallback((): boolean => {
    if (!isSupported || !recognitionRef.current) return false;
    if (isListening) return false;

    setError(null);
    setTranscript('');
    fullTranscriptRef.current = '';
    try {
      recognitionRef.current.start();
      setIsListening(true);
      return true;
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to start speech recognition';
      setError(msg);
      onErrorRef.current?.(msg);
      return false;
    }
  }, [isSupported, isListening]);

  const stop = useCallback(() => {
    clearSilenceTimer();
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.stop();
    } catch {
      // ignore – may already be stopped
    }
    setIsListening(false);
    const finalTranscript = fullTranscriptRef.current;
    fullTranscriptRef.current = '';
    onSpeechEndRef.current?.(finalTranscript);
  }, [clearSilenceTimer]);

  return { start, stop, transcript, isListening, isSupported, error };
}
